'use client';
import {
  AddFavoriteMutation,
  RemoveFavoriteMutation,
  useAddFavoriteMutation,
  useDraftOrderDeleteMutation,
  useDraftOrderLineDeleteMutation,
  useOrderLineUpdateMutation,
  UserDraftOrdersQuery,
  useRemoveFavoriteMutation,
} from '@/graphql';
import convertToReadable from '@/helpers/convertToReadable';
import { favoritesStore } from '@/zustand/favourite';
import { useGuestStore } from '@/zustand/guestStore';
import useUserStore from '@/zustand/useUser';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { Button, Dropdown, Text } from 'rsuite';
import styles from './styles.module.scss';

type dataProps = {
  thumbnail?: any;
  productName?: string;
  variantName?: string;
  productSku?: string;
  totalPrice?: any;
  unitDiscount?: any;
  quantity?: number;
  variant?: {
    pricing?: {
      price: {
        net: {
          amount: number;
        };
      };
      priceUndiscounted: {
        net: {
          amount: number;
        };
      };
    };
  };
};
type CardProps = {
  data: UserDraftOrdersQuery;
  orderId: string;
};

const renderToggle = (props: any) => (
  <button className={styles.dotButton} {...props}>
    <HiOutlineDotsVertical className={styles.dotButtonIcon} />
  </button>
);

export default function GiftCard({
  data: order,
  refetchDraftOrders,
  refetchGuestDraftOrdersData,
  gusetBasketLine,
  refetchGuestDraftOrders,
}: any) {
  const { setFavorites, favorites } = favoritesStore(favorite => favorite);
  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);

  const handleSuccess = (data: AddFavoriteMutation) => {
    if (data?.addFavorite?.errors?.length === 0) {
      setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
    }
  };

  const handleDelete = (data: RemoveFavoriteMutation) => {
    if (data?.removeFavorite?.errors?.length === 0) {
      setFavorites(data.removeFavorite?.user?.favorites?.edges || []);
      refetchDraftOrders?.();
    }
  };

  const [create] = useAddFavoriteMutation({
    onCompleted: handleSuccess,
    context: {
      requiresAuth: true,
    },
  });
  const [deleteDraftOrderMutation] = useDraftOrderDeleteMutation();

  const [remove] = useRemoveFavoriteMutation({
    onCompleted: handleDelete,
    context: {
      requiresAuth: true,
    },
  });
  const [updateProductCount] = useOrderLineUpdateMutation({
    context: {
      requiresAuth: true,
    },
  });
  const [deleteProductFromCart] = useDraftOrderLineDeleteMutation({
    context: {
      requiresAuth: true,
    },
    onCompleted: () => {
      refetchDraftOrders();
    },
  });

  const addToFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await create({
          variables: {
            variant: id,
          },
        });
      } else {
        useGuestStore.getState().addToFavorite(id);
      }
    } catch (error) {
      console.error('Error on add to Favorite', error);
    }
  };

  const removeFromFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await remove({
          variables: {
            variant: id,
          },
        });
      } else {
        useGuestStore.getState().removeFromFavorite(id);
      }
    } catch (error) {
      console.error('Error on Remove from cart: ', error);
    }
  };

  const UpdateProductCount = async (quantityNumber: any) => {
    try {
      await updateProductCount({
        variables: {
          id: order?.id,
          input: {
            quantity: quantityNumber,
          },
        },
      });
    } catch (error) {
      console.error('Error on update product count: ', error);
    }
  };

  const removeFromCart = async (id: any) => {
    try {
      if (gusetBasketLine?.length === 1 && !isLoggedIn) {
        await deleteDraftOrderMutation({
          variables: { id: orderId },
        });
      }

      await deleteProductFromCart({
        variables: { id: id },
      });

      isLoggedIn ? refetchDraftOrders() : refetchGuestDraftOrders();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };
  const t = useTranslations('Components.cart');
  return (
    <div className={styles.card}>
      <Image
        width={140}
        height={140}
        src={order?.media?.[0]?.url}
        alt="order-product"
        className={styles.cardImage}
        style={{
          objectFit: 'contain',
        }}
      />

      <div className={styles.containerContext}>
        <div className={styles.cardWrapper}>
          <div className={styles.cardTitleBox}>
            <div>
              <Text className={styles.cardTitle}>{order?.name}</Text>
              <Text className={styles.productID}>
                {t('product-code')}: {order?.sku}
              </Text>
            </div>
            <Dropdown placement={'leftStart'} renderToggle={renderToggle}>
              <Dropdown.Item>
                <Button className={styles.addFavourites}>
                  {t('to-favorites')}
                </Button>
              </Dropdown.Item>
              <Dropdown.Separator />
              <Dropdown.Item>
                <Button
                  onClick={() => removeFromCart(order?.id)}
                  className={styles.deleteCard}
                >
                  {t('remove')}
                </Button>
              </Dropdown.Item>
            </Dropdown>
          </div>
          <div className={styles.cardPriceBox}>
            {/* <div className={styles.counter}>
              <button
                onClick={() =>
                  order?.quantity === 0
                    ? null
                    : UpdateProductCount(order?.quantity - 1)
                }
                type="button"
                className={styles.minus}
              >
                <FaMinus />
              </button>
              <span className={styles.count}>{order?.quantity}</span>
              <button
                onClick={() => UpdateProductCount(order?.quantity + 1)}
                type="button"
                className={styles.plus}
              >
                <FaPlus />
              </button>
            </div> */}
            <div className={styles.priceWrapper}>
              <p className={styles.price}>{t('gift')}</p>
              <p className={styles.discount}>
                {convertToReadable(order?.pricing?.price?.net?.amount)}{' '}
                {t('price')}
              </p>
            </div>
          </div>
        </div>
        <div className={styles.eventBox}>
          <Button
            onClick={() =>
              favorites?.some(fav => fav?.node?.id === order?.productVariantId)
                ? removeFromFavorite(order.productVariantId)
                : addToFavorite(order?.productVariantId)
            }
            className={`${styles.addFavourites} ${
              favorites?.some(fav => fav?.node?.id === order?.productVariantId)
                ? styles.activeFav
                : styles.nonFav
            }`}
          >
            {favorites?.some(fav => fav?.node?.id === order?.productVariantId)
              ? t('in-favorites')
              : t('to-favorites')}
          </Button>
          <Button
            onClick={() => removeFromCart(order?.id)}
            className={styles.deleteCard}
          >
            {t('remove')}
          </Button>
        </div>
      </div>
    </div>
  );
}
